/**
 * Getting Started (P106) -- the bank-level onboarding ladder.
 *
 * Seven numbered steps whose done-state comes live from the backend
 * (no manual marking). Each card deep-links into the view where the
 * step gets done. The whole surface auto-hides once the bank leaves
 * `fresh` (handled by the nav entry + route guard upstream).
 */
import { Link } from "react-router-dom";
import { Arrow } from "shared/components/Arrow";
import { useTranslation } from "react-i18next";

import { StepProgress } from "shared/components/StepProgress";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { useMe } from "shared/api/useMe";
import { useOnboardingStatus } from "shared/api/useOnboarding";
import { STEP_META } from "shared/journey/stepMeta";

/** Where each step is completed.
 *
 * E1 -- this was a SECOND route map for steps the journey spine
 * already routes, and the two drifted: round 1 renamed the ladder's
 * keys to the spine's (`mvb_declared`, `impact_tolerances`) and this
 * copy kept the old ones, so "Open the IBS register" and its
 * neighbours fell through to "/" -- a call to action that lands on the
 * home page it was pressed from.
 *
 * Title/intent/cta copy lives in i18n under `onboarding.step.<key>`.
 */
function stepRoute(key: string): string {
  return STEP_META[key]?.route ?? "/admin/setup";
}

export function GettingStartedView() {
  const { t } = useTranslation();
  const status = useOnboardingStatus();
  const isAdmin = useMe().data?.is_admin ?? false;

  if (status.isLoading) {
    return (
      <p data-testid="getting-started-loading" className="text-sm text-slate-500">
        {t("onboarding.loading")}
      </p>
    );
  }
  if (status.isError || !status.data) {
    return (
      <p data-testid="getting-started-error" className="text-sm text-rag-red">
        {t("onboarding.error")}
      </p>
    );
  }

  const { steps, completed, total } = status.data;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const allDone = completed === total;

  return (
    <section data-testid="getting-started-view" className="max-w-3xl space-y-5">
      <header>
        <p className="text-[11px] uppercase tracking-wide text-slate-500">
          {t("onboarding.kicker")}
        </p>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="journey"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("onboarding.heading")}
        </h1>
        <p className="mt-1 text-sm text-slate-600">{t("onboarding.intro")}</p>
        {/* Round 12 A7 -- four ladders, none referencing any other, and
            this one and the admin strip both reading "0 of 7" about
            different sevens. The owner chip on every card here reads
            OpsRes owner, CRO / business owner or Compliance; an
            administrator had to read seven of them to work out that not
            one of the seven was his. */}
        <p data-testid="onboarding-whose" className="mt-2 text-sm text-slate-600">
          {t("onboarding.whose")}
        </p>
        <p className="mt-2 flex flex-wrap gap-x-4 text-sm">
          <Link
            to="/admin/setup"
            data-testid="onboarding-journey-link"
            className="font-semibold text-brand-brassDark hover:underline"
          >
            {t("onboarding.wholeJourney")} <Arrow />
          </Link>
          {isAdmin && (
            <Link
              to="/admin"
              data-testid="onboarding-admin-link"
              className="font-semibold text-brand-brassDark hover:underline"
            >
              {t("onboarding.adminLadder")} <Arrow />
            </Link>
          )}
        </p>
      </header>

      <div>
        <div className="mb-1 flex items-baseline justify-between">
          <StepProgress
            done={completed}
            total={total}
            testid="onboarding-progress"
            className="text-xs font-semibold text-slate-600"
          />
          {allDone && (
            <span className="text-xs font-semibold text-rag-green">
              {t("onboarding.allDone")}
            </span>
          )}
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
          <div
            data-testid="onboarding-progress-bar"
            className="h-full rounded-full bg-brand-brass transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <ol className="space-y-3">
        {steps.map((step, i) => (
          <li
            key={step.key}
            data-testid="onboarding-step"
            data-step={step.key}
            data-done={step.done ? "true" : "false"}
            className={`flex items-start gap-3 rounded-lg border p-4 ${
              step.done
                ? "border-rag-greenBg bg-rag-greenBg/40"
                : "border-slate-200 bg-white"
            }`}
          >
            <span
              className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                step.done ? "bg-rag-green text-white" : "bg-slate-100 text-slate-600"
              }`}
            >
              {step.done ? "✓" : i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="font-medium text-slate-900">
                {t(`onboarding.step.${step.key}.title`)}
              </h2>
              <p className="mt-0.5 text-sm text-slate-600">
                {t(`onboarding.step.${step.key}.intent`)}
              </p>
              {/* D10 -- the ladder said what each step was and never
                  whose it is, so an administrator looking at the
                  governance stations could not tell they were somebody
                  else's to do. The Journey has named owners since E3;
                  this is the same fact on the surface people meet
                  first. */}
              {STEP_META[step.key] && (
                <p
                  data-testid={`onboarding-owner-${step.key}`}
                  className="mt-1 font-mono text-[10.5px] uppercase tracking-[.12em] text-slate-400"
                >
                  {t("onboarding.ownedBy", {
                    owner: t(`modules.journey.owner.${STEP_META[step.key]!.owner}`),
                  })}
                </p>
              )}
            </div>
            {step.done ? (
              <span
                data-testid="onboarding-step-done"
                className="flex-shrink-0 self-center text-xs font-semibold uppercase tracking-wide text-rag-green"
              >
                {t("onboarding.done")}
              </span>
            ) : (
              <Link
                data-testid="onboarding-cta"
                to={stepRoute(step.key)}
                className="flex-shrink-0 self-center rounded bg-brand-brassDark px-3 py-1.5 text-sm font-semibold text-white"
              >
                {t(`onboarding.step.${step.key}.cta`)}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
